import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pin, Search, Star, X } from 'lucide-react';

const KIND_LABEL = { image: 'Photo', video: 'Video', audio: 'Audio', document: 'File' };
const snippet = (item) => (item.deleted ? 'This message was deleted' : item.text || KIND_LABEL[item.attachment?.kind] || 'Message');

const Shell = ({ title, icon, onClose, children }) => createPortal(
    <div className="chat-dialog__backdrop" onMouseDown={onClose}>
        <div className="chat-dialog" role="dialog" aria-label={title} onMouseDown={e => e.stopPropagation()}>
            <header>
                <span>{icon}</span>
                <h3>{title}</h3>
                <button type="button" onClick={onClose} aria-label="Close"><X size={16} /></button>
            </header>
            {children}
        </div>
    </div>,
    document.body,
);

/** "Forward to…" — pick the conversation a message should be sent to. targets: [{ id, name, subtitle? }] */
export const ForwardDialog = ({ targets, onPick, onClose, title = 'Forward message to' }) => {
    const [query, setQuery] = useState('');
    const shown = useMemo(() => targets.filter(t => t.name.toLowerCase().includes(query.trim().toLowerCase())), [targets, query]);
    return (
        <Shell title={title} icon={<Search size={16} />} onClose={onClose}>
            <label className="chat-dialog__search"><Search size={14} /><input autoFocus placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} /></label>
            <div className="chat-dialog__list">
                {shown.map(t => (
                    <button key={t.id} type="button" onClick={() => onPick(t)}>
                        <span className="chat-dialog__avatar">{(t.name || '?').charAt(0).toUpperCase()}</span>
                        <span className="chat-dialog__who"><b>{t.name}</b>{t.subtitle && <small>{t.subtitle}</small>}</span>
                    </button>
                ))}
                {shown.length === 0 && <p className="chat-dialog__empty">No conversations to forward to.</p>}
            </div>
        </Shell>
    );
};

/**
 * Starred / pinned messages of the open chat.
 *   kind: 'starred' | 'pinned'
 *   fetchItems(kind) -> resolves an array of message items
 *   onRemove(item)   -> unstar / unpin it (the list reloads afterwards)
 *   labelFor(item)   -> who wrote it ("You", a name…)
 */
export const SavedMessagesDialog = ({ kind, fetchItems, onRemove, labelFor, onClose }) => {
    const [items, setItems] = useState(null);
    const isStar = kind === 'starred';

    const load = async () => { try { setItems(await fetchItems(kind)); } catch (err) { setItems([]); } };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind]);

    return (
        <Shell title={isStar ? 'Starred messages' : 'Pinned messages'} icon={isStar ? <Star size={16} /> : <Pin size={16} />} onClose={onClose}>
            <div className="chat-dialog__list">
                {items === null && <p className="chat-dialog__empty">Loading…</p>}
                {items?.length === 0 && <p className="chat-dialog__empty">{isStar ? 'No starred messages yet. Star one from its menu.' : 'No pinned messages yet. Pin one from its menu.'}</p>}
                {items?.map(item => (
                    <div key={item.id || item._id} className="chat-dialog__saved">
                        <div>
                            <b>{labelFor(item)}</b>
                            <p>{snippet(item)}</p>
                            <small>{new Date(item.at || item.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</small>
                        </div>
                        <button type="button" onClick={async () => { await onRemove(item); load(); }}>{isStar ? 'Unstar' : 'Unpin'}</button>
                    </div>
                ))}
            </div>
        </Shell>
    );
};
